// Single source of truth for per-route <title> + <meta name="description">.
// Used BOTH by the client (PageMeta in App.tsx, via useEffect) AND the
// build-time prerender (prerender.mjs bakes these into each static HTML file
// so crawlers get the right title without executing JS). Keeping them here
// means the two can never drift.

export const DEFAULT_TITLE = 'Giga — AI Workspace for Slack, Teams & ChatGPT';
export const ROUTE_TITLES: Record<string, string> = {
  // ── Public, indexable ──
  '/': 'Giga — AI Coworker, Company Brain, Connected Apps',
  '/ai-coworker': 'Giga — AI Workspace and Company Brain for Slack, Teams & ChatGPT',
  '/pricing': 'Pricing — Giga AI Workspace',
  '/giga-for-chatgpt': 'Giga for ChatGPT — Your Company Brain, Inside ChatGPT',
  '/chatgpt': 'Giga — The Marketing Operating System inside ChatGPT',
  '/company-brain': 'Company Brain — Giga',
  '/viktor-alternative': '8 Best Viktor Alternatives in 2026: Compared',
  '/integrations': 'Integrations — Giga | Connect Your Tools to AI',
  '/careers': 'Careers — Giga | Join Our Team',
  '/contact': 'Contact — Giga | Get in Touch',
  '/support': 'Support — Giga | Get Help',
  '/terms': 'Terms of Service — Giga',
  '/affiliate-terms-reditus': 'Affiliate Partner Terms — Giga',
  '/privacy': 'Privacy Policy — Giga',
  '/vulnerability-disclosure': 'Vulnerability Disclosure — Giga',
  '/demo': 'Giga AI Workspace Demo — See It in Action',
  '/talk-to-sales': 'Talk to Sales — Giga AI Workspace',
  '/creator-program': 'Creator Program — Giga',
  '/developers': 'Giga Developer Resources — API, OpenAPI, Webhooks & MCP',
  '/developers/api': 'Giga API Documentation — Developer Resources',
  '/developers/openapi': 'Giga OpenAPI Documentation — Developer Resources',
  '/developers/authentication': 'Giga API Authentication — Developer Resources',
  '/developers/webhooks': 'Giga Webhook Documentation — Developer Resources',
  '/developers/mcp': 'Giga MCP Documentation — Developer Resources',
  '/new': 'Giga — Infrastructure for AI-Powered Companies',
  // ── Unlisted (noindex) — parked pages, variants, experiments ──
  '/unlisted/security': 'Security — Giga | Enterprise-Grade AI Workspace',
  '/unlisted/enterprise': 'Enterprise AI Workspace — Giga for Organizations',
  '/unlisted/enterprise-platform': 'The Platform for AI Adoption — Giga for Enterprise',
  '/enterprise-platform': 'The Platform for AI Adoption — Giga for Enterprise',
  '/unlisted/blog': 'Blog — Giga | AI Workspace Tips & Insights',
  '/unlisted/research': 'Research — Giga | AI Workspace Research',
  '/unlisted/case-studies': 'Case Studies — Giga AI Workspace',
  '/unlisted/changelog': 'Changelog — Giga | Product Updates',
  '/unlisted/compare/chatgpt': 'Giga vs ChatGPT — AI Workspace Comparison',
  '/unlisted/use-cases/founders-execs': 'AI Workspace for Founders & Execs — Giga',
  '/unlisted/use-cases/client-management': 'AI for Client Management — Giga',
  '/unlisted/use-cases/growth-marketing': 'AI for Growth Marketing — Giga',
  // ── App screen mockups (noindex) ──
  '/app-screen-mockups/signup': 'Sign Up — Giga AI Workspace',
  '/app-screen-mockups/google-consent': 'Google Sign In — Giga',
  '/app-screen-mockups/google-permissions': 'Google Permissions — Giga',
  '/app-screen-mockups/giga-chat': 'Giga Web Chat — Giga',
  '/app-screen-mockups/sign-in': 'Sign In — Giga AI Workspace',
  '/app-screen-mockups/slack-oauth-callback': 'Workspace already connected — Giga',
  '/app-screen-mockups/trial': 'Start Your Free Trial — Giga AI Workspace',
  '/app-screen-mockups/workflow-show': 'Workflow — Scrape Tweet to Notion — Giga',
  '/app-screen-mockups/onboarding-funnel': 'Onboarding conversion — Giga'
};
export const ROUTE_DESCS: Record<string, string> = {
  '/': 'See how Giga connects an AI coworker, company brain, and managed credential vault across your business tools.',
  '/ai-coworker': 'Giga is an AI workspace and company brain for teams. Connect Slack, Teams, ChatGPT, docs, CRM, projects, and business tools so Giga can answer questions and get work done.',
  '/pricing': 'See Giga pricing plans for teams and enterprises. Free trial available.',
  '/giga-for-chatgpt': 'Type @Giga in ChatGPT and the whole company brain comes with it — reads your CRM, repo, books, and docs, then writes the work back.',
  '/chatgpt': 'The marketing operating system for ChatGPT. Connect every marketing tool — CRMs, outreach, enrichment, ads, analytics — and act inside them from @Giga.',
  '/company-brain': 'Bring your company conversations, documents, tools, and expertise into one shared brain with Giga.',
  '/viktor-alternative': 'Compare the best Viktor alternatives for AI automation, integrations, Slack, ChatGPT and Claude. See how Giga, Lindy, Relevance AI, Gumloop and others differ.',
  '/integrations': 'Connect Giga to Slack, Teams, Gmail, Notion, HubSpot, and more. One AI workspace for all your tools.',
  '/careers': 'Join Giga and help build the AI workspace for teams that need reliable, connected work across their tools.',
  '/contact': 'Contact the Giga team for product questions, support, partnerships, and sales conversations.',
  '/support': 'Get help with Giga or the Giga Slack integration. Contact support by email or through the Giga contact form.',
  '/terms': 'Read the Giga terms of service.',
  '/affiliate-terms-reditus': 'Read the Giga affiliate partner terms.',
  '/privacy': 'Read the Giga privacy policy and learn how Giga handles user data.',
  '/vulnerability-disclosure': 'Learn how to report security vulnerabilities to Giga.',
  '/talk-to-sales': 'Talk to a Giga expert about bringing the AI workspace to your team.',
  '/creator-program': 'Learn about the Giga creator program and how creators can earn rewards.',
  '/demo': 'Watch the Giga AI workspace demo. See how it connects your tools and automates work in Slack.',
  '/developers': 'Discover Giga developer resources and current availability for the API, OpenAPI contract, authentication, webhooks, and MCP.',
  '/developers/api': 'Documentation for the anonymous, read-only Giga Integration Catalog API.',
  '/developers/openapi': 'Discover and download the OpenAPI 3.1 contract for the Giga Integration Catalog API.',
  '/developers/authentication': 'Authentication guidance for the anonymous, read-only Giga Integration Catalog API.',
  '/developers/webhooks': 'Current availability and stable documentation URL for Giga webhooks.',
  '/developers/mcp': 'Find Giga public authenticated MCP server documentation, runtime endpoint details, and machine-readable discovery resources.',
  '/new': 'Giga gives AI-powered teams a secure connection vault, auto-updating company brain, and AI coworker that works across Slack, ChatGPT, Claude, and business tools.',
  '/unlisted/security': 'Enterprise-grade security for your AI workspace. Giga never exposes your credentials.',
  '/unlisted/enterprise': 'Enterprise AI workspace with advanced security, SSO, and dedicated support for your organization.',
  '/unlisted/blog': 'Read the latest insights on AI workspaces, productivity, and how teams use Giga.',
  '/unlisted/compare/chatgpt': 'See how Giga compares to ChatGPT. Giga works across your tools, not just in a chat window.'
};

// Route namespaces that must never be indexed by search engines:
//   /unlisted/*            — parked pages, home variants, experiments, internal review
//   /app-screen-mockups/*  — product UI mockups (the live app lives on app.getgiga.com)
// Used by the client (App.tsx PageMeta) AND the prerender (prerender.mjs) to
// bake a <meta name="robots" content="noindex"> tag and to exclude these
// routes from sitemap.xml.
export const NOINDEX_PREFIXES = ['/unlisted', '/app-screen-mockups'];
// `/features/ai-agents/alt` is the archived template rendering of the AI
// Agents page, kept so the old design is not lost. It duplicates the live
// page's content, so it must not compete with it in search.
export const NOINDEX_ROUTES = ['/company-brain', '/enterprise-platform', '/features/ai-agents/alt'];
export function isNoindexRoute(pathname: string): boolean {
  return NOINDEX_ROUTES.includes(pathname) || NOINDEX_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(prefix + '/'));
}

// Match exact path first, then prefix-based fallback for internal routes.
export function resolveRouteMeta(pathname: string): {
  title: string;
  desc?: string;
} {
  if (pathname.startsWith('/features/')) {
    const slug = pathname.slice('/features/'.length);
    // Title-casing the slug gets every feature right except the ones with an
    // acronym in them ("ai-agents" -> "Ai Agents"). Those are named here.
    const NAMED: Record<string, string> = {
      'ai-agents': 'AI Agents',
      'ai-agents/alt': 'AI Agents'
    };
    const name = NAMED[slug] ?? slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    return {
      title: `${name} — Giga Products`,
      desc: `See how ${name} helps your team get more done with Giga.`
    };
  }
  let title = ROUTE_TITLES[pathname];
  const desc = ROUTE_DESCS[pathname];
  if (!title) {
    for (const [prefix, t] of Object.entries(ROUTE_TITLES)) {
      if (prefix !== '/' && pathname.startsWith(prefix)) {
        title = t;
        break;
      }
    }
  }
  return {
    title: title || DEFAULT_TITLE,
    desc
  };
}